/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  Component,
  OnInit,
  ViewChild
}                                from '@angular/core';

import { Router }                from '@angular/router';

import {
  ValidationErrors,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AsyncValidatorFn,
  AbstractControl
}                                from '@angular/forms';

import {
  Observable,
  of
}                                from 'rxjs';

import {
  first,
  map,
  switchMap
} from "rxjs/operators";

import { IUserRow }              from '@/screens/administration/i.user.row';
import { Logger }                from '@/util/logger';
import { bytesToSize }           from "@/util/util";
import { MatSidenav }            from '@angular/material/sidenav';
import { UserService }           from '@/services/user.service';

@Component({
  templateUrl: 'user.detail.page.component.html'
})

/**
 *
 * @class UserDetailPageComponent
 *
 * @brief view and edit a specific user
 *
 * Async Validation tutorial:
 *
 * @link https://careydevelopment.us/blog/how-to-add-an-asynchronous-validator-to-your-angular-form
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2021
 *
 */
export class UserDetailPageComponent implements OnInit {

  @ViewChild('adminsidenav')
  public         nav: MatSidenav;

  /** the userid of the user we are editing */

  private _userid: string    = '';

  /** the existing user attributes on page load and after update click */
  public oldUser: IUserRow   = null;

  /** any changes for the user attributes */
  public newUser: IUserRow   = null;

  /** validation controller for the form */
  public  form: UntypedFormGroup    = null;

  /** validation controller for the form */
  public  resetForm: UntypedFormGroup = null;

  /** users's file count */
  private usage: string      = '-';

  /** user's disk usage */
  private usageBytes: string = '0 B';

  /** confirmation of saving */
  public saveMessage: string = '';

  /** confirmation of password change */
  public resetMessage: string = '';

  /** the new password */
  private _password: string = '';

  /** standard constructor */
  constructor(
    private router: Router,
    private users:  UserService
  ) {

  }

  /** on page setup, create the form validator */
  ngOnInit() {

    /* setup the validation on the form */

    let ud: UserDetailPageComponent = this;

    ud.form = new UntypedFormGroup({

      name: new UntypedFormControl('', [
        Validators.required,
      ]),

      firstname: new UntypedFormControl('', [
        Validators.required,
      ]),

      lastname: new UntypedFormControl('', [
        Validators.required,
      ]),

      email: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$")
        ],
        this.emailExistsValidator()
      ),

      userid: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+$")
        ],
        this.useridExistsValidator()
      )

    });

    ud.form.statusChanges.subscribe((status: string) => {

      Logger.debug(`[UserDetail] user form status: `, status);

    });

    ud.resetForm = new UntypedFormGroup({

      password: new UntypedFormControl('', [
        Validators.required
      ])

    });


    ud.resetForm.statusChanges.subscribe((status: string) => {

      Logger.debug(`[UserDetail] rest form status: `, status);

    });

    /* what user are we editing? The URL ends with the 'userid' of the user */

    const url: string = this.router.url;

    this._userid = url.split(`/`).pop();

    Logger.debug(`[UserDetailPage] looking for user: ${this._userid} ...`);

    ud.users.dump(ud._userid)
      .pipe(first())
      .subscribe(
        (row: IUserRow) => {

          ud.oldUser = Object.assign({}, row);
          ud.newUser = row;

          Logger.debug(`[UserDetail] user: `, ud.newUser);

          /* update the form control */

          ud.patch(ud.newUser, row['usage'], bytesToSize(row['usageBytes']));

        },
        error => {

          Logger.error(`[UserDetail] could not fetch user details: `, error);

        });
  }

  /** helper to update the form display */
  private patch(user: IUserRow, usage, usageBytes) {

    this.form.patchValue({
      name:      user.display_name,
      firstname: user.first_name,
      lastname:  user.last_name,
      email:     user.email,
      userid:    user.userid
    });

    this.usage      = usage;
    this.usageBytes = usageBytes;
  }

  /** server side validation of email, make sure its not already assigned to another user. */
  private emailExistsValidator(): AsyncValidatorFn {

    let ud: UserDetailPageComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((email) => ud.users.userByEmail(email).pipe(
          map((row: IUserRow) => {
            return (row && (row.userid != ud.oldUser.userid)) ? { exists: true } : null;
          })
        ))
      );
    };
  }

  /** server side validation of usuerid, make sure its not already assigned to another user.*/
  private useridExistsValidator(): AsyncValidatorFn {

    let ud: UserDetailPageComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((userid) => ud.users.userByUserid(userid).pipe(
          map((row: IUserRow) => {

              return (row && (row.userid != ud.oldUser.userid)) ? {exists: true} : null;
          })
        ))
      );
    };
  }

  /** helper to update a field in the newUser row */
  updateNewUser(field: string, value: any) {

    this.saveMessage = '';

    if (!this.newUser) {

      return ;
    }

    this.newUser[field] = value;
  }

  /** update the name */
  setName(event: any) {

    this.updateNewUser('display_name', event.target.value);
  }

  /** update the first name */
  setFirstName(event: any) {

    this.updateNewUser('first_name', event.target.value);
  }

  /** update the last name */
  setLastName(event: any) {

    this.updateNewUser('last_name', event.target.value);
  }

  /** update the email */
  setEMail(event: any) {

    this.updateNewUser('email', event.target.value);
  }

  /** update the email */
  setUserid(event: any) {

    this.updateNewUser('userid', event.target.value);
  }

  /** update the password reset value */
  setPassword(event: any) {

    this._password = event.target.value;
  }

  /** getter for display_name */
  get name() { return this.form.get('name'); }

  /** getter for first_name */
  get firstname() { return this.form.get('firstname'); }

  /** getter for last_name */
  get lastname() { return this.form.get('lastname'); }

  /** getter for email */
  get email() { return this.form.get('email'); }

  /** getter for userid */
  get userid() { return this.form.get('userid'); }

  /** getter for password */
  get password() { return this.resetForm.get('password')}

  /** getter for the profile picture */
  profilePicture() {

    if (this.newUser && this.newUser.options && this.newUser.options.picture) {

      return this.newUser.options.picture;
    }

    return 'assets/images/profile-512.png';
  }

  /** getter for user's' file count */
  filesCount() {

    return this.usage;
  }

  /** getter for user's disk usage */
  diskUsage() {

    return this.usageBytes;
  }

  /** open or close the side nav panel */
  toggleSidebar() {

    this.nav.toggle();
  }

  /** update the database */
  save() {

    Logger.debug(`[UserDetail] saving: `, this.newUser);

    let ud: UserDetailPageComponent = this;
    let edit: any = {};

    ['userid', 'email', 'display_name', 'last_name', 'first_name'].forEach( (field) => {

      if (ud.newUser[field] == ud.oldUser[field]) {

        /* hasn't changed */

        return;
      }

      edit[field] = ud.newUser[field];

    });

    if (Object.keys(edit).length == 0) {

      /* no changes */

      ud.saveMessage = "No changes.";

      return ;
    }

    ud.users.update(ud.oldUser.userid, edit)
      .pipe(first())
      .subscribe(
        (row: IUserRow) => {

          Logger.debug(`[UserDetail] saved: `, row);

          ud.oldUser     = Object.assign({}, row);
          ud.newUser     = row;

          /* update the form control */

          ud.patch(ud.newUser, row['usage'], bytesToSize(row['usageBytes']));

          ud.saveMessage = "Saved!";
        },
        error => {

          Logger.error(`[UserDetail] could not save: `, error);

          ud.saveMessage = error.toString();

        });

  }

  /** reset the user's password */
  public resetPassword() {

    Logger.debug(`[UserDetail] resetting password: ${this._password}`);

    let ud: UserDetailPageComponent = this;

    ud.users.update(ud.oldUser.userid, {password: this._password} as IUserRow)
      .pipe(first())
      .subscribe(
        (row: IUserRow) => {

          Logger.debug(`[UserDetail] password reset! `, row);

          ud.oldUser     = Object.assign({}, row);
          ud.newUser     = row;

          /* update the form control */

          ud.patch(ud.newUser, row['usage'], bytesToSize(row['usageBytes']));

          ud.resetMessage = "Password has been set!";

          ud._password     = '';
        },
        error => {

          Logger.error(`[UserDetail] could not set password: `, error);

          ud.resetMessage = error.toString();

        });


  }
}
